import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { ProjectItem, Todo, ProjectDetails, InvitationItem, Response } from '../../types';
import { getNotificationInstance } from '../utils/notification';

const baseQuery = fetchBaseQuery({ baseUrl: '/api/projects' });

// @ts-ignore
const baseQueryWithErrorHandling = async (args, api, extraOptions) => {
    const notification = getNotificationInstance();

    const result = await baseQuery(args, api, extraOptions);
    if (result.error) {
        if (notification) {
            notification.error({
                message: 'Ошибка',
                description: 'Произошла ошибка при выполнении запроса',
                placement: 'topRight',
            });
        }
        else {
            alert('Произошла ошибка при выполнении запроса');
        }
    }
    return result;
};

export const projectsSlice = createApi({
    reducerPath: 'projectsApi',

    baseQuery: baseQueryWithErrorHandling,

    tagTypes: ['Projects', 'Invites'],

    endpoints: (builder) => ({
        getProjects: builder.query<Response<Array<ProjectItem>>, void>({
            query: () => ({
                url: '/get-projects-list',
                method: 'POST',
                body: {},
            }),
            providesTags: ['Projects'],
        }),

        getProjectDetails: builder.query<Response<ProjectDetails>, { projectId: number }>({
            query: (request) => ({
                url: '/get-project-details',
                method: 'POST',
                body: request,
            }),
            providesTags: (result, error, arg) => [{ type: 'Projects', id: arg.projectId }],
        }),

        createProject: builder.mutation<Response<any>, { name: string; type: number }>({
            query: (request) => ({
                url: '/create-project',
                method: 'POST',
                body: request,
            }),
            invalidatesTags: ['Projects'],
        }),

        addInvitation: builder.mutation<Response<any>, { projectId: number; username: string; role: number }>({
            query: (request) => ({
                url: '/add-invitation',
                method: 'POST',
                body: request,
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Projects', id: arg.projectId }],
        }),

        deleteInvitation: builder.mutation<Response<any>, { projectId: number; username: string }>({
            query: (request) => ({
                url: '/delete-invitation',
                method: 'POST',
                body: request,
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Projects', id: arg.projectId }],
        }),

        acceptInvitation: builder.mutation<Response<any>, { projectId: number }>({
            query: (request) => ({
                url: '/accept-invitation',
                method: 'POST',
                body: request,
            }),
            invalidatesTags: ['Invites', 'Projects'],
        }),

        rejectInvitation: builder.mutation<Response<any>, { projectId: number }>({
            query: (request) => ({
                url: '/reject-invitation',
                method: 'POST',
                body: request,
            }),
            invalidatesTags: ['Invites'],
        }),

        removeUserFromProject: builder.mutation<Response<any>, { projectId: number; userId: number }>({
            query: (request) => ({
                url: '/remove-user-from-project',
                method: 'POST',
                body: request,
            }),
            invalidatesTags: ['Projects'],
        }),

        getInvitationsList: builder.query<Response<InvitationItem[]>, void>({
            query: () => ({
                url: '/get-invitations-list',
                method: 'POST',
                body: {},
            }),
            providesTags: ['Invites'],
        }),
    }),
});

export const {
useGetProjectsQuery,
    useGetProjectDetailsQuery,
    useGetInvitationsListQuery,
    useCreateProjectMutation,
    useAddInvitationMutation,
    useDeleteInvitationMutation,
    useAcceptInvitationMutation,
    useRejectInvitationMutation,
    useRemoveUserFromProjectMutation,
} = projectsSlice;

